'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { ExperienceFeedPreviewBanner } from '@/components/experience/experience-feed-preview-banner';
import { ProductMedia } from '@/components/product-media';
import { isProductVideoUrl } from '@/lib/productMedia';
import type { Product } from '@/lib/productService';
import { cn } from '@/lib/utils';

type ExperienceCatalogBannerProps = {
  normalizedLocale: string;
  publicStoreId: string;
  locale: string;
  products: Product[];
  promoMediaUrl?: string | null;
  className?: string;
};

/**
 * Catalog entry point to Experience Feed.
 * Uses the merchant's custom catalog promo media when configured; otherwise falls back
 * to the dynamic animated preview banner.
 */
export function ExperienceCatalogBanner({
  normalizedLocale,
  publicStoreId,
  locale,
  products,
  promoMediaUrl,
  className,
}: ExperienceCatalogBannerProps) {
  const t = useTranslations('catalog.experience');
  const trimmedPromoMediaUrl = promoMediaUrl?.trim() || null;

  if (trimmedPromoMediaUrl) {
    const isVideo = isProductVideoUrl(trimmedPromoMediaUrl);
    const experiencePath = `/${normalizedLocale}/${publicStoreId}/experience`;

    return (
      <Link
        href={experiencePath}
        aria-label={t('catalogBannerCta')}
        className={cn(
          'group block overflow-hidden rounded-4xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className
        )}
      >
        <div className="relative aspect-[3/4] w-full max-h-[520px] bg-muted sm:max-h-none">
          <ProductMedia
            src={trimmedPromoMediaUrl}
            alt={t('catalogBannerTitle')}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            sizes="100vw"
            autoPlay={isVideo}
            loop={isVideo}
            muted={isVideo}
            playsInline={isVideo}
          />
        </div>
      </Link>
    );
  }

  return (
    <ExperienceFeedPreviewBanner
      normalizedLocale={normalizedLocale}
      publicStoreId={publicStoreId}
      locale={locale}
      products={products}
      className={className}
    />
  );
}
